import { ApiError } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { Form } from '@frontend/components/forms';
import { EnumSelectListV2 } from '@frontend/components/select';
import { ICONS } from '@frontend/icons';

import { useAppDispatch } from '../../redux/store';
import { useUpdateModal } from './update-modal.controller';

export interface UpdateModalProps<T, S> {
    object?: T;
    order: (keyof S | Array<keyof S>)[];
    required?: (keyof S | Array<keyof S>)[];
    onSubmit: (value: S) => Promise<T>;
    expectedType?: Map<keyof S, 'object' | 'boolean' | 'number' | 'string' | 'entity' | 'textarea'>;
    enumOverwrite?: Map<keyof S, keyof typeof EnumSelectListV2>;
    fieldProps?: (formValues: { [key: string]: any }) => Map<keyof S, any>;
    formId?: string;
    submitDisabled?: (formValues: { [key: string]: any }) => boolean;
    submitButtonOverride?: React.ReactNode;
    append?: React.ReactNode;
    type: EntityType;
    title?: React.ReactNode;
    label?: React.ReactNode;
    keyOverwrite?: Map<keyof S, React.ReactNode>;
    subForms?: Map<
        React.ReactNode,
        { order: (keyof S | Array<keyof S>)[]; expectedType?: Map<keyof S, 'object' | 'boolean' | 'number' | 'string' | 'entity' | 'textarea'> }
    >;
    values: S;
    successMessageOverwrite?: React.ReactNode;
    errorMessageOverwrite?: (error: ApiError) => React.ReactNode;
    onSubmitSuccess?: (values: T) => void;
}

const UpdateModal = <T extends object, S extends object>(props: UpdateModalProps<T, S>) => {
    const viewProps = useUpdateModal<T, S>(props);

    return (
        <Form<T, S>
            {...props}
            dispatch={useAppDispatch()}
            type={props.type}
            value={viewProps.value}
            onChange={viewProps.setValue}
            show={props.order}
            required={props.required}
            bodyClassname='modal bg-background-default p-6 h-full gap-6 flex flex-col rounded-xl'
            headerClassName='modal-header px-0 py-0'
            cardBodyClassName='self-stretch p-4 bg-background-surface rounded-2xl border inline-flex flex-col justify-start items-start border-border-default'
            footerClassName='flex justify-end w-full items-start gap-3'
            label={
                <div className='inline-flex w-full items-center justify-between self-stretch'>
                    <div className='text-foreground-default justify-start text-3xl font-semibold'>{props.label}</div>
                    <div
                        onClick={viewProps.closeTopModal}
                        className='bg-background-surface hover:bg-brand-light border-border-default flex size-11 cursor-pointer items-center justify-center self-center rounded-xl border'>
                        <ICONS.BUTTON.CLOSE_2 className='text-brand-muted size-6' />
                    </div>
                </div>
            }
            onSubmit={props.onSubmit}
            onSubmitSuccess={(obj) => {
                props.onSubmitSuccess && props.onSubmitSuccess(obj);
                viewProps.closeTopModal();
            }}
            expectedType={props.expectedType}
            fieldProps={props.fieldProps ? props.fieldProps(viewProps.formValues) : undefined}
            id={viewProps.formId}
            useStaticId
            submitDisabled={props.submitDisabled ? props.submitDisabled(viewProps.formValues) : false}
            submitButtonOverride={props.submitButtonOverride}
            append={props.append}
        />
    );
};

export default UpdateModal;
